import React, { useCallback } from 'react';
import { VStack, Box } from '@chakra-ui/react';
import PageHeader from '@/components/ui/PageHeader';
import SearchAndFiltersSection from '@/components/section/SearchAndFiltersSection';
import EntityTableSection from '@/components/section/EntityTableSection';
import LoadingSkeleton from '@/components/LoadingSkeleton';
import ErrorAlert from '@/components/ErrorAlert';
import Pagination from '@/components/ui/Pagination';
import { Users } from 'lucide-react';
import { 
  useGetUsersQuery,
  User,
  useUserActions,
  useUserFilters,
  createUserColumns,
  createUserActions,
  UserDetailsModal,
  ChangeEmailModal,
  ForgotPasswordModal,
  ChangePasswordModal,
  ChangeRolesModal,
  ChangeProfileSlugModal,
} from '@/entities/users';
import { UserEditModal } from '@/entities/users/components/UserCreateEditModal';
import CloneProfileModal from '@/entities/users/components/CloneProfileModal';
import CopyConditionsModal from '@/entities/users/components/CopyConditionsModal';
import ManageDoctorsModal from '@/entities/users/components/ManageDoctorsModal';
import EditConfigurationModal from '@/entities/users/components/EditConfigurationModal';
import { CreateNewAssistantHashModal } from '@/entities/users/components/CreateNewAssistantModal';
import { getFilterRoleOptions } from '@/entities/users/constants';
import { useGetOrganizationsQuery, useLazyGetOrganizationsQuery } from '@/entities/organizations/api';
import { SearchableSelectOption } from '@/components/ui/SearchableSelect';


const UsersPage: React.FC = () => {
  // Filters
  const {
    search,
    organizationId,
    roles,
    setSearch,
    setOrganizationId,
    setRoles,
    setPage,
    setPerPage,
    filters,
    hasActiveFilters,
  } = useUserFilters();

  // Data fetching
  const {
    data: usersResponse,
    isLoading,
    isError,
    error,
    isFetching,
    refetch,
  } = useGetUsersQuery(filters, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });

  const { data: organizationsData } = useGetOrganizationsQuery({});
  const [searchOrganizationsApi] = useLazyGetOrganizationsQuery();

  // Extract users array from paginated response
  const usersArray = usersResponse?.data || [];
  const totalUsers = usersResponse?.total || 0;

  // Search function for organizations - calls API with search parameter
  const searchOrganizations = useCallback(async (query: string): Promise<SearchableSelectOption[]> => {
    try {
      // Call API with search parameter
      const response = await searchOrganizationsApi({
        search: query.trim(),
        per_page: 50, // Limit results for performance
      }).unwrap();

      const organizationOptions = response.data?.map(org => ({
        value: org.id.toString(),
        label: org.name,
      })) || [];

      return organizationOptions;
    } catch (error) {
      console.error('Error searching organizations:', error);
      // Fallback to cached data if API call fails
      const organizationOptions = organizationsData?.data?.map(org => ({
        value: org.id.toString(),
        label: org.name,
      })) || [];
      
      return organizationOptions;
    }
  }, [searchOrganizationsApi, organizationsData]);

  // Get selected organization option for the searchable select
  const selectedOrganizationOption = React.useMemo(() => {
    if (!organizationId) return null;
    
    const organization = organizationsData?.data?.find(org => org.id === organizationId);
    return organization ? {
      value: organization.id.toString(),
      label: organization.name,
    } : null;
  }, [organizationId, organizationsData]);

  // Actions
  const {
    handleAction,
    handleRowClick,
    handleCreateAssistant,
    handleImpersonateUser,
    handleImpersonateById,
    isImpersonatingUser,
  } = useUserActions();

  // Table configuration
  const columns = createUserColumns();
  const actions = createUserActions(handleAction, isLoading, {
    onImpersonate: handleImpersonateUser,
    isImpersonating: isImpersonatingUser,
  });

  // Filter options
  const roleOptions = getFilterRoleOptions();

  const organizationOptions = organizationsData?.data?.map(org => ({
    value: org.id.toString(),
    label: org.name,
  })) || [];

  // Create filter configs for SearchAndFiltersSection
  const searchFilters = [
    {
      key: 'roles',
      label: 'All Roles',
      value: roles.length > 0 ? roles[0] : '', // For now, single select
      options: [{ value: '', label: 'All Roles' }, ...roleOptions],
      onChange: (value: string) => setRoles(value ? [value] : []),
    },
    {
      key: 'organization',
      label: 'All Organizations',
      value: organizationId?.toString() || '',
      options: [{ value: '', label: 'All Organizations' }, ...organizationOptions],
      onChange: (value: string) => setOrganizationId(value ? Number(value) : undefined),
      searchable: true,
      onSearch: searchOrganizations,
      selectedOption: selectedOrganizationOption,
    },
  ];

  // Memoize handlers
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value);
    },
    [setSearch]
  );

  const handlePageChange = useCallback((page: number) => {
    setPage(page);
  }, [setPage]);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setPerPage(pageSize);
  }, [setPerPage]);

  // Loading state
  if (isLoading && !usersArray.length) {
    return <LoadingSkeleton rows={5} />;
  }

  if (isError) {
    return (
      <ErrorAlert
        isError={true}
        title="Failed to load users"
        message={error?.toString() || "An error occurred while loading data"}
        onRetry={refetch}
      />
    );
  }

  return (
    <Box maxW="full" mx="auto" pb={8} minH="calc(100vh - 4rem)" display="flex" flexDirection="column">
      <VStack gap={6} align="stretch" flex="1" minH="0">
        <PageHeader
          title="Users Management"
          subtitle={`${totalUsers} total users`}
          onAddNew={handleCreateAssistant}
          addButtonText="Create Assistant"
          onRefresh={refetch}
          isRefreshing={isFetching}
        />

        <SearchAndFiltersSection
          searchValue={search}
          searchPlaceholder="Search users by name, email, or slug..."
          onSearchChange={handleSearchChange}
          filters={searchFilters}
        />
        
        <Box flex="1" minH="0">
          <EntityTableSection<User>
            data={usersArray}
            columns={columns}
            actions={actions}
            isLoading={isFetching}
            onRowClick={handleRowClick}
            emptyState={{
              icon: Users,
              title: "No users found",
              description: hasActiveFilters 
                ? "Try adjusting your search or filters"
                : "No users have been created yet",
            }}
          />
        </Box>

        {/* Pagination */}
        {usersResponse && (
          <Pagination
            paginationInfo={{
              current_page: usersResponse.current_page,
              last_page: usersResponse.last_page,
              per_page: usersResponse.per_page,
              total: usersResponse.total,
            }}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            isLoading={isFetching}
          />
        )}

      {/* Hash-managed Drawers - each drawer self-determines if it should render */}
      <UserDetailsModal
        onImpersonateUser={(userId) => {
          void handleImpersonateById(userId);
        }}
        isImpersonatingUser={isImpersonatingUser}
      />

      <CreateNewAssistantHashModal
        onSuccess={() => refetch()}
      />

      <UserEditModal
        onSuccess={() => refetch()}
      />

      <ChangeEmailModal
        onSuccess={() => refetch()}
      />

      <ForgotPasswordModal
        onSuccess={() => refetch()}
      />

      <ChangePasswordModal
        onSuccess={() => refetch()}
      />

      <ChangeRolesModal
        onSuccess={() => refetch()}
      />

      <ChangeProfileSlugModal
        onSuccess={() => refetch()}
      />

      <CloneProfileModal
        onSuccess={() => refetch()}
      />

      <CopyConditionsModal
        onSuccess={() => refetch()}
      />

      <ManageDoctorsModal
        onSuccess={() => refetch()}
      />

      <EditConfigurationModal
        onSuccess={() => refetch()}
      />
      </VStack>
    </Box>
  );
};

export default UsersPage; 
